// Payment Phase 2/3 — the paywall. Fires on a 402 `needs_paid_cv` at the 2nd-read / craft / export triggers. Calm,
// serious register (Principle 5) — a consultant's unlock, not a hype pitch. Currency CAD. Two checkout CTAs (CA$19 one
// CV / CA$49 three CVs) → LemonSqueezy hosted checkout; plus "use a credit" when the account holds bundle credits.
// FIREWALL: pure UI over the entitlement endpoints — touches no CV content.
// Polish B2 — rebuilt on the shared <ModalShell> (focus trap incl. Tab cycling, Escape, scrim-click, scroll lock,
// safe-area padding, role=dialog aria-modal, initial/return focus) — it previously hand-rolled a bare scrim div
// with none of that. All existing testids + behavior kept.
import { useEffect, useState, type CSSProperties } from 'react';
import { api } from './lib/api';
import { save as saveCheckoutContext } from './lib/checkoutContext';
import { ModalShell } from './components/ModalShell';

export function PaywallModal({ cvId, onClose, onUnlocked, checkoutReturn }: {
  cvId: string;                  // the CV to unlock (carried from the 402)
  onClose: () => void;           // dismiss without unlocking
  onUnlocked: () => void;        // a credit was redeemed → the CV is now paid (caller refetches / re-tries)
  // Revenue P0 (P0-1) — what to restore to on the /checkout/success return trip. Optional so existing callers
  // that haven't been updated still compile; without it the return trip just can't auto-restore the view.
  checkoutReturn?: { view: string; guidedMode?: 'author' | 'refine' };
}) {
  const [credits, setCredits] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { void (async () => {
    const r = await api<{ credits_available: number }>('/api/lemonsqueezy/entitlement');
    if (r.ok) setCredits(r.data.credits_available);
  })(); }, []);

  async function checkout(tier: 'single' | 'cycle') {
    setBusy(true); setError('');
    // Revenue P0 (P0-1) — persist where to come back to BEFORE the redirect: window.location replaces the whole
    // page (killing any React state), so the client /checkout/success route needs this to restore the user.
    // `tier` is the poll's INTENT: on a 'single' return trip the poll must NOT spend a bundle credit on this cv.
    saveCheckoutContext({ view: checkoutReturn?.view ?? 'author', cvId, tier, guidedMode: checkoutReturn?.guidedMode });
    const r = await api<{ url?: string; error?: string }>('/api/lemonsqueezy/checkout', { method: 'POST', body: JSON.stringify({ tier, cv_id: cvId }) });
    setBusy(false);
    if (r.ok && r.data.url) { window.location.href = r.data.url; return; } // → LemonSqueezy hosted checkout
    setError('Checkout is unavailable right now — please try again.');
  }

  async function useCredit() {
    setBusy(true); setError('');
    const r = await api<{ ok?: boolean }>('/api/lemonsqueezy/redeem', { method: 'POST', body: JSON.stringify({ cv_id: cvId }) });
    setBusy(false);
    if (r.ok) { onUnlocked(); return; }
    setError('Couldn’t apply a credit — please try again.');
  }

  return (
    <ModalShell onClose={onClose} label="Unlock this CV" testId="paywall-modal">
      <h2 style={S.h}>Unlock this CV</h2>
      <p style={S.body}>
        Your first review is free. To <strong>re-evaluate</strong>, <strong>craft</strong>, <strong>tailor to your
        programs</strong>, and <strong>export</strong> this CV, unlock it.
      </p>
      {credits != null && credits > 0 && (
        <button className="btn btn-primary" style={S.cta} disabled={busy} onClick={useCredit} data-testid="paywall-use-credit">
          Use 1 of your {credits} credit{credits === 1 ? '' : 's'} to unlock this CV
        </button>
      )}
      <button className="btn btn-primary" style={S.cta} disabled={busy} onClick={() => checkout('single')} data-testid="paywall-single">
        Unlock this CV — CA$19
      </button>
      <button className="btn btn-secondary" style={S.cta} disabled={busy} onClick={() => checkout('cycle')} data-testid="paywall-cycle">
        3 CVs — CA$49
      </button>
      {error && <p style={S.err} data-testid="paywall-error">{error}</p>}
      <button className="btn btn-ghost" style={S.dismiss} onClick={onClose} data-testid="paywall-dismiss">Not now</button>
      <p style={S.fine}>One-time payment, no subscription. Secure checkout via LemonSqueezy.</p>
    </ModalShell>
  );
}

const S: Record<string, CSSProperties> = {
  h: { fontFamily: 'var(--serif)', fontSize: 'var(--fs-h1)', color: 'var(--ink)', margin: '0 0 var(--space-2)' },
  body: { fontSize: 'var(--fs-body)', color: 'var(--ink-subtle)', lineHeight: 'var(--lh-body)', margin: '0 0 var(--space-4)' },
  cta: { display: 'block', width: '100%', margin: 'var(--space-2) 0', textAlign: 'center' },
  dismiss: { display: 'block', margin: '10px auto 0', fontSize: 'var(--fs-small)' },
  err: { color: 'var(--err, #B42318)', fontSize: 'var(--fs-small)', margin: 'var(--space-2) 0 0' },
  fine: { fontSize: 'var(--fs-label)', color: 'var(--muted)', textAlign: 'center', margin: 'var(--space-3) 0 0' },
};
